"use client";

import type { LogEvent, LogLevel } from "~/hooks/useLogEvent";
import { getInAppWebview } from "~/services/api/detect-webview";
import {
    classifyFetchFailure,
    isBenignFetchFailure,
    type FetchFailureReason,
} from "~/services/api/fetch-failure";
import type { FetchJsonResponse } from "~/services/api/types/fetch-json-response";
import type { Tokens } from "~/services/api/types/tokens";
import { getAnsweredStatus } from "~/services/api/wrapper-fetch-json-response";
import { isTokenExpired } from "~/services/auth/tokenExpiry";
import {
    allowlistedErrorName,
    describeError,
    NON_ERROR_THROWN_NAME,
    redactPersonalData,
} from "~/utils/errorTelemetry";
import { PaymentMethodEnum } from "~/types/Payment";
import type { ChargebeeBillingAddressMetadata } from "./buildChargebeeBillingAddress";
import {
    CARD_TOKENIZE_FAILURE_CATEGORY,
    getAllowlistedProviderErrorCode,
    getCardTokenizeErrorContext,
    getFailureDetails,
    type CardTokenizeErrorContext,
    type CardTokenizeFailureCategory,
} from "./checkoutErrorClassification";
import { UNKNOWN_FAILURE_REASON } from "./resolveFailureReason";

/**
 * Checkout submit-lifecycle instrumentation.
 *
 * Reading and classifying vendor errors lives in
 * `checkoutErrorClassification.ts`; this module only emits.
 *
 * Emits a correlated Datadog log funnel so we can see WHERE the authenticated /
 * guest checkout breaks client-side:
 *   checkout_validation_failed | checkout_tokenize_start → checkout_tokenize_result
 *   → checkout_submit_start → checkout_response {httpStatus} → thankyou_navigated
 * All events carry the same `attemptId` so the funnel can be stitched per attempt
 * and dropped-off attempts (got a 200 but never reached thank-you) are visible.
 *
 * Every event declares a `level`: failures go out as warn/error so monitors and
 * `status:error` dashboards pick them up. Info-level events are queryable but
 * surfaced by neither, so a failure left at info is effectively unmonitored.
 *
 * None of these become Error Tracking issues. `useLogEvent` sends a message and
 * flat context, never the `Error` itself, so the logs carry no `error.kind` or
 * `error.stack` for Error Tracking to ingest and group on — only the global
 * handlers in `DataDogLogs.tsx` do that. Read these in Log Explorer.
 *
 * Convention (see docs/log_for_FE/TAG-TAXONOMY.md): snake_case event/message
 * names, kebab-case tag value. Emission is prod-gated inside `useLogEvent`.
 */

/**
 * One domain marker for every checkout log. Everything that describes the
 * event lives in orthogonal attributes below; encoding severity, payment
 * method or component in the tag makes those dimensions impossible to group
 * independently in Datadog.
 */
export const CHECKOUT_LOG_TAG = "checkout";
export const CHECKOUT_TELEMETRY_SCHEMA_VERSION = 2;

/**
 * Stable Datadog event names. Keep all checkout log messages here so callers
 * cannot introduce a new aggregation key with an inline string.
 */
export const CHECKOUT_LOG_EVENT = {
    ADDRESS_AUTOCOMPLETE_FAILED: "checkout_address_autocomplete_failed",
    ADDRESS_AUTOCOMPLETE_INCOMPLETE:
        "checkout_address_autocomplete_incomplete",
    ANALYTICS_PAYLOAD_BUILD_FAILED:
        "checkout_analytics_payload_build_failed",
    CAPTCHA_UNAVAILABLE: "checkout_captcha_unavailable",
    CARD_CTA_CLICKED: "checkout_card_cta_clicked",
    CARD_FIELDS_MOUNT_TIMEOUT: "checkout_card_fields_mount_timeout",
    CARD_FIELDS_READY_AFTER_TIMEOUT: "checkout_card_fields_ready_after_timeout",
    CART_ITEMS_UNSERIALIZABLE: "checkout_cart_items_unserializable",
    CART_PRICE_NOT_LOADED: "checkout_cart_price_not_loaded",
    CART_STORAGE_UNUSABLE: "checkout_cart_storage_unusable",
    CHARGEBEE_CONFIG_MISSING: "checkout_chargebee_config_missing",
    CHARGEBEE_GLOBAL_MISSING: "checkout_chargebee_global_missing",
    CHARGEBEE_INIT_FAILED: "checkout_chargebee_init_failed",
    CHARGEBEE_INSTANCE_MISSING: "checkout_chargebee_instance_missing",
    CONTACT_ANALYTICS_FAILED: "checkout_contact_analytics_failed",
    COUPON_APPLY_RESULT: "checkout_coupon_apply_result",
    COUPON_RESOLUTION_TIMEOUT: "checkout_coupon_resolution_timeout",
    DUPLICATE_REPLAYED: "checkout_duplicate_replayed",
    EXPRESS_SUBMIT_FAILED: "checkout_express_submit_failed",
    EXTERNAL_SCRIPT_LOAD_FAILED: "checkout_external_script_load_failed",
    EXTERNAL_SCRIPT_RETRY_STARTED: "checkout_external_script_retry_started",
    GOOGLE_PAY_READINESS_FAILED: "checkout_google_pay_readiness_failed",
    GOOGLE_PAY_READINESS_RESOLVED: "checkout_google_pay_readiness_resolved",
    GOOGLE_PAY_REQUEST_REWRITE_MISSING:
        "checkout_google_pay_request_rewrite_missing",
    INITIAL_PRODUCTS_FAILED: "checkout_initial_products_failed",
    ORDER_SNAPSHOT_FAILED: "checkout_order_snapshot_failed",
    OVERLAY_PAGE_EXIT: "checkout_overlay_page_exit",
    OVERLAY_STUCK: "checkout_overlay_stuck",
    PAGE_VIEWED: "checkout_page_viewed",
    PAYMENT_INTENT_RETRY_FAILED: "checkout_payment_intent_retry_failed",
    PAYMENT_INTENT_RETRY_STARTED: "checkout_payment_intent_retry_started",
    PAYMENT_REJECTED: "checkout_payment_rejected",
    PAYMENT_SOURCE_UNAVAILABLE: "checkout_payment_source_unavailable",
    PENDING_PURCHASE_CAPTURE_FAILED:
        "checkout_pending_purchase_capture_failed",
    PENDING_SUBSCRIPTION_EXTERNAL_ID_MISSING:
        "checkout_pending_subscription_missing_external_id",
    POST_CHARGE_ANALYTICS_FAILED:
        "checkout_post_charge_analytics_failed",
    POST_CHARGE_SIDE_EFFECT_FAILED:
        "checkout_post_charge_side_effect_failed",
    PRODUCT_FETCH_FAILED: "checkout_product_fetch_failed",
    PRODUCTS_UNAVAILABLE: "checkout_products_unavailable",
    RESPONSE: "checkout_response",
    SDK_READY: "checkout_sdk_ready",
    STATE_INELIGIBLE: "checkout_state_ineligible",
    STEP_ADVANCED: "checkout_step_advanced",
    SUBMIT_BLOCKED: "checkout_submit_blocked",
    SUBMIT_ERROR: "checkout_submit_error",
    SUBMIT_RETRY: "checkout_submit_retry",
    SUBMIT_START: "checkout_submit_start",
    THANK_YOU_NAVIGATED: "checkout_thankyou_navigated",
    THANK_YOU_REDIRECT_FAILED: "checkout_thankyou_redirect_failed",
    TOKENIZE_RESULT: "checkout_tokenize_result",
    TOKENIZE_START: "checkout_tokenize_start",
    VALIDATION_FAILED: "checkout_validation_failed",
    WALLET_CHARGE_AMOUNT_MISMATCH:
        "checkout_wallet_charge_amount_mismatch",
    WALLET_CTA_CLICKED: "checkout_wallet_cta_clicked",
    WALLET_HANDLER_RESPONSE: "checkout_wallet_handler_response",
    WALLET_HANDOFF_STUCK: "checkout_wallet_handoff_stuck",
    WALLET_INTENT_RECOVERED: "checkout_wallet_intent_recovered",
    WALLET_INTENT_RECOVERY_BLOCKED:
        "checkout_wallet_intent_recovery_blocked",
    WALLET_INTENT_RECOVERY_FAILED:
        "checkout_wallet_intent_recovery_failed",
    WALLET_INTENT_RECOVERY_SUPERSEDED:
        "checkout_wallet_intent_recovery_superseded",
    WALLET_MOUNT_ATTEMPT_FAILED: "checkout_wallet_mount_attempt_failed",
    WALLET_MOUNT_FAILED: "checkout_wallet_mount_failed",
    WALLET_MOUNT_SKIPPED: "checkout_wallet_mount_skipped",
    WALLET_PAYMENT_FAILED: "checkout_wallet_payment_failed",
    // A failure we could not describe. The payment outcome is still reported
    // and the customer still gets their checkout back; this names the step
    // whose read of the error threw, so the blind spot is visible instead of
    // looking like a tap that never failed.
    WALLET_PAYMENT_FAILURE_UNREADABLE:
        "checkout_wallet_payment_failure_unreadable",
    WALLET_PREREQUISITES_MISSING:
        "checkout_wallet_prerequisites_missing",
    WALLET_REARM_FAILED: "checkout_wallet_rearm_failed",
    WALLET_RECOVERY_FAILED: "checkout_wallet_recovery_failed",
    WALLET_REFRESH_ATTEMPT_FAILED:
        "checkout_wallet_refresh_attempt_failed",
    WALLET_REFRESH_BLOCKED: "checkout_wallet_refresh_blocked",
    WALLET_REFRESH_EXHAUSTED: "checkout_wallet_refresh_exhausted",
    WALLET_REFRESH_INTENT_FAILED:
        "checkout_wallet_refresh_intent_failed",
    WALLET_REFRESH_LOAD_FAILED: "checkout_wallet_refresh_load_failed",
    WALLET_REFRESH_MOUNT_FAILED: "checkout_wallet_refresh_mount_failed",
    WALLET_REFRESH_WATCHDOG_TIMEOUT:
        "checkout_wallet_refresh_watchdog_timeout",
    WALLET_SUBMIT_BLOCKED: "checkout_wallet_submit_blocked",
    WALLET_SUBMIT_FAILED_AFTER_AUTHORIZATION:
        "checkout_wallet_submit_failed_after_authorization",
    WALLET_TAP_REPORT_FAILED: "checkout_wallet_tap_report_failed",
    WALLET_TTL_REFRESH_FAILED: "checkout_wallet_ttl_refresh_failed",
    WALLET_TTL_REFRESH_STARTED: "checkout_wallet_ttl_refresh_started",
} as const;

export type CheckoutLogEvent =
    (typeof CHECKOUT_LOG_EVENT)[keyof typeof CHECKOUT_LOG_EVENT];

export const CHECKOUT_RESULT = {
    STARTED: "started",
    SUCCESS: "success",
    FAILURE: "failure",
    BLOCKED: "blocked",
    DISMISSED: "dismissed",
    CANCELLED: "cancelled",
} as const;

export type CheckoutResult =
    (typeof CHECKOUT_RESULT)[keyof typeof CHECKOUT_RESULT];

export const CHECKOUT_STAGE = {
    PAGE: "page",
    CONTACT: "contact",
    VALIDATION: "validation",
    PRODUCTS: "products",
    COUPON: "coupon",
    SDK_LOAD: "sdk_load",
    CARD_FIELDS: "card_fields",
    WALLET_MOUNT: "wallet_mount",
    WALLET_REFRESH: "wallet_refresh",
    PAYMENT_INTENT: "payment_intent",
    TOKENIZE: "tokenize",
    AUTHORIZE: "authorize",
    SUBMIT: "submit",
    REDIRECT: "redirect",
    POST_PURCHASE: "post_purchase",
    STORAGE: "storage",
    ADDRESS: "address",
    PAYLOAD: "payload",
} as const;

export type CheckoutStage =
    (typeof CHECKOUT_STAGE)[keyof typeof CHECKOUT_STAGE];

export const CHECKOUT_COMPONENT = {
    PAGE: "checkout_page",
    FORM: "checkout_form",
    PRODUCT_LOADER: "product_loader",
    COUPON: "coupon",
    CHARGEBEE_SDK: "chargebee_sdk",
    HOSTED_CARD_FIELDS: "hosted_card_fields",
    PAYMENT_INTENT: "payment_intent",
    CHECKOUT_API: "checkout_api",
    WALLET_HANDLER: "wallet_handler",
    EXTERNAL_SCRIPT: "external_script",
    PAYLOAD_BUILDER: "payload_builder",
    ORDER_SNAPSHOT: "order_snapshot",
    PENDING_PURCHASE: "pending_purchase",
    CART_STORAGE: "cart_storage",
    ADDRESS_AUTOCOMPLETE: "address_autocomplete",
    NAVIGATION: "navigation",
    OVERLAY: "overlay",
} as const;

export type CheckoutComponent =
    (typeof CHECKOUT_COMPONENT)[keyof typeof CHECKOUT_COMPONENT];

export const CHECKOUT_FAILURE_BOUNDARY = {
    CLIENT: "client",
    INNERBALANCE_API: "innerbalance_api",
    /** Vercel's edge answered (e.g. a challenge page); our API never did. */
    VERCEL_EDGE: "vercel_edge",
    NETWORK: "network",
    CHARGEBEE: "chargebee",
    STRIPE: "stripe",
    BROWSER: "browser",
    THIRD_PARTY: "third_party",
    STORAGE: "storage",
} as const;

export type CheckoutFailureBoundary =
    (typeof CHECKOUT_FAILURE_BOUNDARY)[keyof typeof CHECKOUT_FAILURE_BOUNDARY];

export const CHECKOUT_FAILURE_CATEGORY = {
    VALIDATION: "validation",
    ISSUER_DECLINE: "issuer_decline",
    NETWORK: "network",
    API: "api",
    /** An edge page came back in place of our API's JSON. */
    EDGE_INTERCEPTION: "edge_interception",
    SDK: "sdk",
    SCRIPT: "script",
    PAYLOAD: "payload",
    STATE: "state",
    STORAGE: "storage",
    NAVIGATION: "navigation",
    TIMEOUT: "timeout",
    CONFIGURATION: "configuration",
    DATA: "data",
    UNKNOWN: "unknown",
} as const;

export type CheckoutFailureCategory =
    (typeof CHECKOUT_FAILURE_CATEGORY)[keyof typeof CHECKOUT_FAILURE_CATEGORY];

/**
 * Who a failed request to our API (payment intent, coupon) is filed against.
 * An edge page (a Vercel challenge) answering in our API's place is Vercel's;
 * no HTTP answer (no `answeredStatus`) is a network failure; anything else our
 * API refused.
 */
export function resolveApiFailureAttribution(
    answeredStatus: number | undefined,
    edgeIntercepted: boolean
): {
    failureBoundary: CheckoutFailureBoundary;
    failureCategory: CheckoutFailureCategory;
} {
    if (edgeIntercepted) {
        return {
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.VERCEL_EDGE,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.EDGE_INTERCEPTION,
        };
    }

    if (answeredStatus === undefined) {
        return {
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.NETWORK,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.NETWORK,
        };
    }

    return {
        failureBoundary: CHECKOUT_FAILURE_BOUNDARY.INNERBALANCE_API,
        failureCategory: CHECKOUT_FAILURE_CATEGORY.API,
    };
}

export const CHECKOUT_CUSTOMER_IMPACT = {
    NONE: "none",
    DEGRADED: "degraded",
    CHECKOUT_BLOCKED: "checkout_blocked",
    PAYMENT_FAILED: "payment_failed",
    CHARGED_WITHOUT_CONFIRMATION: "charged_without_confirmation",
} as const;

export type CheckoutCustomerImpact =
    (typeof CHECKOUT_CUSTOMER_IMPACT)[keyof typeof CHECKOUT_CUSTOMER_IMPACT];

export type CheckoutType = "guest" | "auth" | "upsell";
export type CheckoutPaymentFlow = "standard" | "express";

export type CheckoutTelemetryData = {
    result: CheckoutResult;
    stage: CheckoutStage;
    component: CheckoutComponent;
    checkoutType?: CheckoutType;
    paymentFlow?: CheckoutPaymentFlow;
    paymentMethod?: string;
    failureBoundary?: CheckoutFailureBoundary;
    failureCategory?: CheckoutFailureCategory | CardTokenizeErrorContext["failureCategory"];
    reason?: string;
    customerImpact?: CheckoutCustomerImpact;
    attemptId?: string | null;
} & Record<string, unknown>;

/**
 * Datadog's log-based Error Tracking only opens an issue for an error-level log
 * that carries `error.kind` or `error.stack`. A stack or an `Error` would ship
 * the raw message, which may quote customer details; a plain `{ kind }` built
 * from our own allow-listed names cannot.
 *
 * Without a message or stack, the kind is what issues group on, so it names
 * the event and the failure category rather than the JS error class — every
 * `TypeError` in checkout would otherwise collapse into one issue.
 */
function resolveErrorTrackingKind(
    message: CheckoutLogEvent,
    failureCategory: CheckoutTelemetryData["failureCategory"]
): string {
    return failureCategory ? `${message}:${failureCategory}` : message;
}

/**
 * The only supported emitter for checkout logs. Besides keeping the domain
 * tag stable, this forces every event to declare the three dimensions every
 * dashboard needs and stamps the payload with a migration-safe schema version.
 */
export function logCheckoutEvent(
    logEvent: LogEvent,
    event: {
        message: CheckoutLogEvent;
        level: LogLevel;
        data: CheckoutTelemetryData;
    }
): void {
    logEvent({
        message: event.message,
        level: event.level,
        tag: CHECKOUT_LOG_TAG,
        ...(event.level === "error"
            ? {
                error: {
                    kind: resolveErrorTrackingKind(
                        event.message,
                        event.data.failureCategory
                    ),
                },
            }
            : {}),
        data: {
            telemetrySchemaVersion: CHECKOUT_TELEMETRY_SCHEMA_VERSION,
            ...event.data,
        },
    });
}

export type CheckoutFlow = "auth" | "guest" | "express" | "upsell";
export type CheckoutHttpStatus = number | "network_error";

function checkoutDimensions(
    flow: CheckoutFlow,
    paymentFlowOverride?: CheckoutPaymentFlow
): {
    checkoutType: CheckoutType;
    paymentFlow: CheckoutPaymentFlow;
} {
    if (flow === "express") {
        return { checkoutType: "auth", paymentFlow: "express" };
    }

    return {
        checkoutType: flow,
        paymentFlow: paymentFlowOverride ?? "standard",
    };
}

export type CardAuthorizeTelemetry = {
    authorizeDurationMs: number;
    chargebeeReactWrapperVersion: string;
    billingAddressMetadata: ChargebeeBillingAddressMetadata;
};

/**
 * Where a tokenize attempt spent its time. Splits `durationMs` so a slow
 * failure is charged to one boundary: our create-payment-intent call or the
 * browser↔ChargeBee↔Stripe authorize. Each field is reported only once its
 * phase has started, so a missing `authorizeDurationMs` means authorize was
 * never reached. The phases do not sum to `durationMs` — the remainder is our
 * own pre-flight.
 */
export type TokenizePhaseTelemetry = {
    /** create-payment-intent: our API and the network to it. */
    intentDurationMs: number;
    /** How stale the intent was at authorize: minutes on the wallet path. */
    intentAgeAtAuthorizeMs: number;
};

/**
 * The state of Chargebee's hosted card form at the moment PURCHASE was pressed.
 * Exists to test whether the 41-second `invalid_request` hang is hosted fields
 * that never finished registering, leaving a session that cannot tokenize.
 */
export type CardFieldsTelemetry = {
    /**
     * Which fields reported a complete, error-free value. All three complete
     * means the browser↔Chargebee channel was alive up to the click.
     */
    cardFieldsComplete: Record<string, boolean>;
    /**
     * Bootstrap cost, measured from the start of the checkout visit rather than
     * document navigation, which client-side routing inflates.
     */
    cardFieldsReadyAtVisitMs: number;
    /** Button-ready to click. Near zero races field registration. */
    cardReadyToClickMs: number;
};

/**
 * Where the wallet sheet was when ChargeBee rejected. Exists to tell a sheet
 * the customer closed (no `cancel` callback for it) from an SDK that failed
 * while the sheet was up, for rejections that carry no code at all.
 */
export type WalletSheetTelemetry = {
    /** `success` fired: the sheet authorized and handed the payment back. */
    sheetHandedBack: boolean;
    /** `hidden` means the customer had left the page or the app. */
    pageVisibility: DocumentVisibilityState;
};

/**
 * A coupon `get-coupon` accepted that can no longer be redeemed. Only the
 * checkout POST knows the customer, so this verdict arrives at submit.
 */
const COUPON_REDEMPTION_PATTERN = /already been redeemed|no more redemptions/;

/**
 * Verdicts our API returns on something the customer chose. Matched on the
 * conclusion rather than the word "coupon", so a malformed coupon request is
 * still reported as our defect.
 */
const BUSINESS_REJECTION_PATTERN = new RegExp(
    `${COUPON_REDEMPTION_PATTERN.source}|cannot be applied|not eligible|not applicable`
);

export function isCouponRedemptionRejection(message: unknown): boolean {
    return (
        typeof message === "string" &&
        COUPON_REDEMPTION_PATTERN.test(message.toLowerCase())
    );
}
/**
 * The level a rejected checkout submit (HTTP 422) should be logged at. One
 * endpoint reports both a total we computed wrong and a card the issuer
 * refused through the same `message` field, so `error` is reserved for
 * rejections that should have been impossible; anything the customer or their
 * bank decided is `warn`.
 */
export function resolveCheckoutRejectionLevel(message: unknown): LogLevel {
    const text = typeof message === "string" ? message : String(message ?? "");

    if (BUSINESS_REJECTION_PATTERN.test(text.toLowerCase())) return "warn";

    // Reuses the gateway vocabulary the tokenize path already classifies on, so
    // "(card_declined) … insufficient funds" is recognised in one place only.
    const { failureCategory } = getCardTokenizeErrorContext(new Error(text));

    return failureCategory === CARD_TOKENIZE_FAILURE_CATEGORY.ISSUER_DECLINE
        ? "warn"
        : "error";
}

/**
 * Level for a gateway verdict during tokenize.
 *
 * `warn` is expected behavior we still want a count of: the bank refused the
 * card, including one that had expired in a wallet, or the customer mistyped
 * a hosted field. `info` drops the event out of the monitors. `error` is a
 * verdict that should have been impossible — a wallet has no number or CVC to
 * mistype, so the same validation category there is our payload, not theirs.
 */
export function resolveGatewayFailureLevel(
    failureCategory: CardTokenizeFailureCategory | undefined,
    paymentMethod?: string
): Extract<LogLevel, "warn" | "error"> {
    if (failureCategory === CARD_TOKENIZE_FAILURE_CATEGORY.ISSUER_DECLINE) {
        return "warn";
    }

    if (
        failureCategory === CARD_TOKENIZE_FAILURE_CATEGORY.CARD_VALIDATION &&
        paymentMethod === PaymentMethodEnum.CARD
    ) {
        return "warn";
    }

    return "error";
}

/**
 * How the tokenize stage ended. `INTENT_ERROR` vs `ERROR` is the key split:
 * our own API/network boundary against the browser↔ChargeBee↔Stripe gateway.
 * The values are a wire contract queried by string in saved Datadog searches,
 * so the tests assert on the literals instead of importing from here.
 */
export const TOKENIZE_OUTCOME = {
    /** Gateway authorized the card; submit follows. */
    SUCCESS: "success",
    /** Our API's create-payment-intent returned non-200. */
    INTENT_FAILED: "intent_failed",
    /**
     * create-payment-intent threw or got no HTTP response: our API/network
     * failed before the gateway.
     */
    INTENT_ERROR: "intent_error",
    /** create-payment-intent was answered by Vercel's edge, not our API. */
    INTENT_INTERCEPTED: "intent_intercepted",
    /** cardRef was null — the card component was not mounted. */
    CARD_NOT_MOUNTED: "card_not_mounted",
    /** authorizeWith3ds resolved falsy — the gateway gave no result. */
    NO_RESPONSE: "no_response",
    /** authorizeWith3ds threw, gateway declines included. */
    ERROR: "error",
    /** Wallet only: the customer closed the sheet without authorizing. */
    DISMISSED: "dismissed",
    /**
     * Wallet only: we refused to submit an authorized wallet payment because
     * our own state could not be trusted (cart price not loaded). Not a
     * gateway failure — we stopped it.
     */
    BLOCKED: "blocked",
    /**
     * Wallet only: we refused to submit an authorized wallet payment because
     * the wallet's shipping address cannot be shipped to (non-US, no state,
     * no street line or ZIP). The customer's data, not a defect.
     */
    ADDRESS_REJECTED: "address_rejected",
} as const;

export type TokenizeOutcome =
    (typeof TOKENIZE_OUTCOME)[keyof typeof TOKENIZE_OUTCOME];

/**
 * What a wallet `dismissed` outcome was read from. Only `cancel` is the wallet
 * saying the customer closed the sheet; the other two are rejections read as a
 * closed sheet, and stay countable apart in case ChargeBee starts failing the
 * same way. A wire contract like `TOKENIZE_OUTCOME`.
 */
export const WALLET_DISMISSAL_SOURCE = {
    /** ChargeBee's `cancel` callback. */
    CANCEL: "cancel",
    /** A Google Pay rejection carrying `CANCELED`. */
    GOOGLE_PAY_CANCELED: "google_pay_canceled",
    /** An Apple Pay rejection: a bare `inited` intent with no payment attempt. */
    UNATTEMPTED_INTENT: "unattempted_intent",
} as const;

export type WalletDismissalSource =
    (typeof WALLET_DISMISSAL_SOURCE)[keyof typeof WALLET_DISMISSAL_SOURCE];

/**
 * Outcomes that are not a problem: authorized, or the customer decided not to
 * pay. Levelling ~400 monthly wallet dismissals as failures would bury the
 * attempts that genuinely broke.
 */
const NON_FAILURE_TOKENIZE_OUTCOMES = new Set<TokenizeOutcome>([
    TOKENIZE_OUTCOME.SUCCESS,
    TOKENIZE_OUTCOME.DISMISSED,
]);

/**
 * The most recently minted attempt, for the reporters that fire outside a
 * submit and so have no id to be handed one.
 *
 * Module-level rather than threaded through the checkout store, because the
 * only callers are watchdogs that fire long after the call stack that minted
 * the id has gone. "Last minted" is the right reading in both directions: a
 * new attempt supersedes an abandoned one, and a finished attempt is still the
 * one a stuck overlay is holding the customer on.
 *
 * `null` until the first PURCHASE click, which is the finding in itself — it
 * separates a checkout that broke before anything was attempted from one that
 * broke during an attempt we can name.
 */
let lastMintedAttemptId: string | null = null;

/**
 * The attempt a non-submit reporter should attribute itself to, or `null` if
 * the visit has not produced one yet.
 */
export function getCurrentCheckoutAttemptId(): string | null {
    return lastMintedAttemptId;
}

/**
 * Distinguishes two fallback ids minted in the same millisecond, which is what
 * a timestamp alone cannot do. Only reached when `randomUUID` is missing.
 */
let fallbackAttemptSequence = 0;

/**
 * The tail of a fallback id. `getRandomValues` is available wherever
 * `randomUUID` is not — the latter needs a secure context, the former does
 * not — so it separates two documents minting at the same instant; the
 * counter separates two mints within one document.
 *
 * Not `Math.random`, which read as a security question to every scanner that
 * walked past it. Uniqueness here is for correlating log records and is never
 * a secret, but a line that has to be defended in a comment costs more over
 * time than one that raises nothing.
 */
function fallbackAttemptSuffix(cryptoObj: Crypto | undefined): string {
    fallbackAttemptSequence += 1;
    const sequence = fallbackAttemptSequence.toString(36);

    if (!cryptoObj?.getRandomValues) return sequence;

    const [noise] = cryptoObj.getRandomValues(new Uint32Array(1));

    return `${(noise ?? 0).toString(36)}${sequence}`;
}

/** Stable id correlating submit_start → response → thankyou_navigated. */
export function newCheckoutAttemptId(): string {
    const cryptoObj =
        typeof globalThis !== "undefined"
            ? (globalThis.crypto as Crypto | undefined)
            : undefined;

    lastMintedAttemptId = cryptoObj?.randomUUID
        ? cryptoObj.randomUUID()
        : `att-${Date.now().toString(36)}-${fallbackAttemptSuffix(cryptoObj)}`;

    return lastMintedAttemptId;
}

/** Effective connection type (e.g. "4g", "3g") when the browser exposes it. */
export function getConnectionType(): string | undefined {
    if (typeof navigator === "undefined") return undefined;
    const conn = (
        navigator as Navigator & { connection?: { effectiveType?: string } }
    ).connection;
    return conn?.effectiveType;
}

function deviceContext(): Record<string, unknown> {
    if (typeof navigator === "undefined") return {};
    return {
        userAgent: navigator.userAgent,
        connection: getConnectionType(),
        inAppWebview: getInAppWebview(navigator.userAgent),
    };
}

/**
 * The message of a thrown failure, redacted. Kept because "(card_declined)
 * Your card has insufficient funds" is what an RCA opens the log for; redacted
 * because the wording is the thrower's and may quote the customer back.
 */
function serializeError(error: unknown): string | undefined {
    if (error === undefined || error === null) return undefined;
    // ChargeBee rejects a wallet payment with the whole payment intent, which
    // `String()` renders as "[object Object]". Only its allow-listed code is
    // logged: the gateway's prose is free text redaction cannot clear of a
    // name or a street.
    if (
        typeof error === "object" &&
        allowlistedErrorName(error) === NON_ERROR_THROWN_NAME
    ) {
        return (
            getAllowlistedProviderErrorCode(getFailureDetails(error).code) ??
            UNKNOWN_FAILURE_REASON
        );
    }
    // Via `describeError`: a gateway SDK may reject with a value that throws on
    // `instanceof` and `String()` alike, which must not break this handler.
    return describeError(error).errorMessage;
}

/**
 * Prose our API wrote, redacted because the API quotes the offending value
 * back. Absence is preserved rather than named: "no wording" and "wording we
 * removed" are different findings.
 */
function serializeServerMessage(message: string | undefined): string | undefined {
    return message === undefined ? undefined : redactPersonalData(message);
}

/**
 * Session state at the instant a checkout POST is fired, which makes the 401
 * bucket attributable: `use-fetch-base` sends the bearer whatever the device
 * clock says, so a 401 here is the backend's verdict on that token.
 * The token itself is never part of this — only whether one was present.
 */
export type TokenStateTelemetry = {
    hasToken: boolean;
    tokenExpires: number | null;
    /**
     * Milliseconds until expiry, signed: negative means already expired at
     * submit time, by that much. Seconds is a refresh race; days is an idle
     * session nobody revalidated on load.
     */
    tokenExpiresInMs: number | null;
    /** Device-clock verdict — see `isTokenExpired` for the caveat. */
    isExpiredClientSide: boolean;
};

/** Reads a token snapshot into the loggable shape above. */
export function describeTokenState(
    tokens: Tokens | null | undefined
): TokenStateTelemetry {
    const rawTokenExpires = tokens?.tokenExpires;
    const tokenExpires =
        typeof rawTokenExpires === "number" &&
        Number.isFinite(rawTokenExpires) &&
        rawTokenExpires > 0
            ? rawTokenExpires
            : null;
    return {
        hasToken: Boolean(tokens?.token),
        tokenExpires,
        tokenExpiresInMs:
            tokenExpires === null ? null : tokenExpires - Date.now(),
        isExpiredClientSide: isTokenExpired(tokens),
    };
}

/**
 * Checkout POST is about to be sent. In the card flow the `attemptId` is the
 * one minted at the PURCHASE click (tokenize stage), so the whole funnel
 * shares a single id.
 *
 * Token state is optional because the guest path emits this before signup:
 * reporting `hasToken: false` for every guest would read as an auth outage.
 */
export function logCheckoutSubmitStart(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        flow: CheckoutFlow;
        paymentFlow?: CheckoutPaymentFlow;
        priceItemSlug?: string;
        paymentIntentId?: string;
        /** Joins the attempt to the backend's checkout log, see `checkoutFlowId.ts`. */
        checkoutFlowId?: string;
        paymentMethod?: string;
        userId?: string | null;
        hasCoupon?: boolean;
    } & Partial<TokenStateTelemetry>
): void {
    const { flow, paymentFlow, ...rest } = payload;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.SUBMIT_START,
        level: "info",
        data: {
            result: CHECKOUT_RESULT.STARTED,
            stage: CHECKOUT_STAGE.SUBMIT,
            component: CHECKOUT_COMPONENT.CHECKOUT_API,
            ...checkoutDimensions(flow, paymentFlow),
            ...rest,
            ...deviceContext(),
        },
    });
}

/**
 * Checkout POST settled (any outcome, including thrown requests reported as
 * `network_error`). 4xx+/network failures go out as warn so they are not
 * sampled away.
 */
export function logCheckoutResponse(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        flow: CheckoutFlow;
        paymentFlow?: CheckoutPaymentFlow;
        httpStatus: CheckoutHttpStatus;
        durationMs: number;
        userId?: string | null;
        paymentIntentId?: string;
        /**
         * Part of `durationMs` the page spent hidden. Close to all of it means
         * the browser suspended the page, not that the request was slow.
         */
        pageHiddenMs?: number;
        /** Repeats sent after a lost response, see `sendCheckoutRequest`. */
        retries?: number;
        /**
         * Time the lost requests and waits took, kept out of `durationMs` so
         * that stays one request's latency.
         */
        retryMs?: number;
        /** A repeat was due but the replay window may have closed. */
        retryWindowExpired?: boolean;
        /** The backend returned a checkout it had already processed. */
        duplicateRequest?: boolean;
        error?: unknown;
    }
): void {
    const { error, flow, paymentFlow, ...rest } = payload;
    const failed =
        payload.httpStatus === "network_error" || payload.httpStatus >= 400;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.RESPONSE,
        level: failed ? "warn" : "info",
        data: {
            result: failed ? CHECKOUT_RESULT.FAILURE : CHECKOUT_RESULT.SUCCESS,
            stage: CHECKOUT_STAGE.SUBMIT,
            component: CHECKOUT_COMPONENT.CHECKOUT_API,
            ...checkoutDimensions(flow, paymentFlow),
            ...(failed
                ? {
                    failureBoundary:
                          payload.httpStatus === "network_error"
                              ? CHECKOUT_FAILURE_BOUNDARY.NETWORK
                              : CHECKOUT_FAILURE_BOUNDARY.INNERBALANCE_API,
                    failureCategory:
                          payload.httpStatus === "network_error"
                              ? CHECKOUT_FAILURE_CATEGORY.NETWORK
                              : CHECKOUT_FAILURE_CATEGORY.API,
                    reason:
                          payload.httpStatus === "network_error"
                              ? "network_error"
                              : "http_error",
                    customerImpact:
                          CHECKOUT_CUSTOMER_IMPACT.PAYMENT_FAILED,
                }
                : {}),
            ...rest,
            ...(error === undefined || error === null
                ? {}
                : {
                    error: serializeError(error),
                    errorName: allowlistedErrorName(error),
                }),
            ...deviceContext(),
        },
    });
}

/**
 * Why a submit attempt failed, once a request had actually been fired. One
 * vocabulary across the card, express/wallet and upsell paths, so a single
 * Datadog query covers all three. A wire contract, like `TOKENIZE_OUTCOME`.
 */
export const SUBMIT_ERROR_REASON = {
    /** A response with no branch to handle it: 401, 5xx, a remapped challenge. */
    UNEXPECTED_STATUS: "unexpected_status",
    /**
     * The request got no HTTP response — network, abort, offline — whether it
     * threw or came back as the transport-failure 503.
     */
    REQUEST_THREW: "request_threw",
    /**
     * The submit's own code threw, before the request or while handling its
     * answer. The request itself never throws past `sendCheckoutRequest`.
     */
    CLIENT_THREW: "client_threw",
    /**
     * Guest only: reCAPTCHA threw before the request, which is a stub
     * `grecaptcha` an ad-blocker left behind. Nothing was sent.
     */
    CAPTCHA_THREW: "captcha_threw",
} as const;

export type SubmitErrorReason =
    (typeof SUBMIT_ERROR_REASON)[keyof typeof SUBMIT_ERROR_REASON];

const SUBMIT_ERROR_DIMENSIONS: Record<
    SubmitErrorReason,
    {
        failureBoundary: CheckoutFailureBoundary;
        failureCategory: CheckoutFailureCategory;
    }
> = {
    [SUBMIT_ERROR_REASON.UNEXPECTED_STATUS]: {
        failureBoundary: CHECKOUT_FAILURE_BOUNDARY.INNERBALANCE_API,
        failureCategory: CHECKOUT_FAILURE_CATEGORY.API,
    },
    [SUBMIT_ERROR_REASON.REQUEST_THREW]: {
        failureBoundary: CHECKOUT_FAILURE_BOUNDARY.NETWORK,
        failureCategory: CHECKOUT_FAILURE_CATEGORY.NETWORK,
    },
    [SUBMIT_ERROR_REASON.CLIENT_THREW]: {
        failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
        failureCategory: CHECKOUT_FAILURE_CATEGORY.UNKNOWN,
    },
    [SUBMIT_ERROR_REASON.CAPTCHA_THREW]: {
        failureBoundary: CHECKOUT_FAILURE_BOUNDARY.THIRD_PARTY,
        failureCategory: CHECKOUT_FAILURE_CATEGORY.SCRIPT,
    },
};

/**
 * The status a submit reports for a checkout response. `useFetchBase` turns a
 * rejected `fetch` into the transport-failure 503 of `wrapperFetchJsonResponse`
 * rather than a throw, so most lost responses arrive here and not in the
 * request's `.catch`. Reporting that stand-in 503 would blame our API for a
 * request it may have answered with 200.
 */
export function checkoutResponseHttpStatus<T>(
    response: FetchJsonResponse<T>
): CheckoutHttpStatus {
    return getAnsweredStatus(response) === undefined
        ? "network_error"
        : response.status;
}

/** The reason for a response no submit branch handles. */
export function unhandledResponseReason(
    httpStatus: CheckoutHttpStatus
): SubmitErrorReason {
    return httpStatus === "network_error"
        ? SUBMIT_ERROR_REASON.REQUEST_THREW
        : SUBMIT_ERROR_REASON.UNEXPECTED_STATUS;
}

/**
 * A submit attempt that failed (always level error). Not every one is visible
 * to the customer: the upsell path reports an unexpected status while showing
 * nothing at all.
 */
export function logCheckoutSubmitError(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        flow: CheckoutFlow;
        paymentFlow?: CheckoutPaymentFlow;
        reason: SubmitErrorReason;
        httpStatus?: CheckoutHttpStatus;
        userId?: string | null;
        /**
         * Present when the failed submit carried an authorized intent: a
         * `request_threw` with one is a customer who may have been charged.
         */
        paymentIntentId?: string;
        /** See `logCheckoutResponse`. */
        pageHiddenMs?: number;
        /** See `logCheckoutResponse`. */
        retries?: number;
        /** See `logCheckoutResponse`. */
        retryWindowExpired?: boolean;
        error?: unknown;
    }
): void {
    const { error, flow, paymentFlow, reason, ...rest } = payload;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.SUBMIT_ERROR,
        level: "error",
        data: {
            result: CHECKOUT_RESULT.FAILURE,
            stage: CHECKOUT_STAGE.SUBMIT,
            component: CHECKOUT_COMPONENT.CHECKOUT_API,
            ...checkoutDimensions(flow, paymentFlow),
            ...SUBMIT_ERROR_DIMENSIONS[reason],
            reason,
            customerImpact: CHECKOUT_CUSTOMER_IMPACT.PAYMENT_FAILED,
            ...rest,
            error: serializeError(error),
            ...deviceContext(),
        },
    });
}

/**
 * Our own code threw after the charge went through. Warn: the purchase is
 * done and the customer is not told anything failed, so only reporting is
 * degraded. Kept out of `checkout_submit_error`, which counts submits we
 * could not complete.
 */
export function logCheckoutPostChargeSideEffectFailed(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        flow: CheckoutFlow;
        paymentFlow?: CheckoutPaymentFlow;
        paymentMethod?: string;
        userId?: string | null;
        error: unknown;
    }
): void {
    const { error, flow, paymentFlow, ...rest } = payload;
    const { errorName, errorMessage } = describeError(error);

    logCheckoutEvent(logEvent, {
        level: "warn",
        message: CHECKOUT_LOG_EVENT.POST_CHARGE_SIDE_EFFECT_FAILED,
        data: {
            result: CHECKOUT_RESULT.FAILURE,
            stage: CHECKOUT_STAGE.POST_PURCHASE,
            component: CHECKOUT_COMPONENT.PAYLOAD_BUILDER,
            ...checkoutDimensions(flow, paymentFlow),
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.UNKNOWN,
            reason: "post_charge_side_effect_failed",
            customerImpact: CHECKOUT_CUSTOMER_IMPACT.DEGRADED,
            ...rest,
            error: errorMessage,
            errorName,
            ...deviceContext(),
        },
    });
}

/**
 * A checkout request lost its response and is about to be sent again. Warn,
 * not info, so it is not sampled away: every one is a customer whose order
 * may already exist.
 */
export function logCheckoutSubmitRetry(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        flow: CheckoutFlow;
        paymentFlow?: CheckoutPaymentFlow;
        /** 1 for the first repeat. */
        retry: number;
        userId?: string | null;
        paymentIntentId?: string;
        error?: unknown;
    }
): void {
    const { error, flow, paymentFlow, ...rest } = payload;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.SUBMIT_RETRY,
        level: "warn",
        data: {
            result: CHECKOUT_RESULT.STARTED,
            stage: CHECKOUT_STAGE.SUBMIT,
            component: CHECKOUT_COMPONENT.CHECKOUT_API,
            ...checkoutDimensions(flow, paymentFlow),
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.NETWORK,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.NETWORK,
            reason: SUBMIT_ERROR_REASON.REQUEST_THREW,
            customerImpact: CHECKOUT_CUSTOMER_IMPACT.DEGRADED,
            ...rest,
            ...(error === undefined || error === null
                ? {}
                : {
                    error: serializeError(error),
                    errorName: allowlistedErrorName(error),
                }),
            ...deviceContext(),
        },
    });
}

/**
 * The backend answered with a checkout it had already processed
 * (`duplicateRequest: true`): nothing new was charged.
 */
export function logCheckoutDuplicateReplayed(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        flow: CheckoutFlow;
        paymentFlow?: CheckoutPaymentFlow;
        retries: number;
        /**
         * The replayed order is this tab's, so this submit reports its
         * purchase. `false` is another checkout's, see `isReplayOfAnotherCheckout`.
         */
        ownCheckout: boolean;
        /**
         * Guest only: whether the replay signed the guest in. `false` is a
         * guest who reaches thank-you signed out.
         */
        sessionIssued?: boolean;
        userId?: string | null;
        paymentIntentId?: string;
    }
): void {
    const { flow, paymentFlow, ...rest } = payload;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.DUPLICATE_REPLAYED,
        level: "warn",
        data: {
            result: CHECKOUT_RESULT.SUCCESS,
            stage: CHECKOUT_STAGE.SUBMIT,
            component: CHECKOUT_COMPONENT.CHECKOUT_API,
            ...checkoutDimensions(flow, paymentFlow),
            customerImpact:
                payload.sessionIssued === false
                    ? CHECKOUT_CUSTOMER_IMPACT.DEGRADED
                    : CHECKOUT_CUSTOMER_IMPACT.NONE,
            ...rest,
            ...deviceContext(),
        },
    });
}

/** A form section's errors, keyed by field name. */
export type FieldErrors = Record<string, string | undefined>;

/**
 * The names of the fields that are in error, prefixed by section. Names only:
 * the messages quote the customer's own name, address and card number back,
 * and already live on the BigQuery `purchase_validation_failed` event.
 */
function invalidFieldNames(sections: Record<string, FieldErrors>): string[] {
    return Object.entries(sections).flatMap(([section, errors]) =>
        Object.entries(errors ?? {})
            .filter(([, message]) => !!message)
            .map(([field]) => `${section}.${field}`)
    );
}

/**
 * Where in the checkout the form refused to advance. `contact` has no payment
 * attempt behind it — nothing tokenized, nothing charged — so a query that
 * mixes the two steps is not comparing like with like.
 */
export const VALIDATION_STEP = {
    /** The multi-step flow's first step: contact and shipping details. */
    CONTACT: "contact",
    /** The card form, on the click that would have tokenized. */
    PAYMENT: "payment",
} as const;

export type ValidationStep =
    (typeof VALIDATION_STEP)[keyof typeof VALIDATION_STEP];

/**
 * Client-side form validation blocked the submit. `sections` is what makes it
 * actionable: without it, a Chargebee card field that has started refusing
 * every valid input looks identical to a forgotten postcode. `attemptId` is
 * absent for the contact step — an attempt is minted at tokenize time, so a
 * submit the first step never let through has no funnel to join.
 */
export function logCheckoutValidationFailed(
    logEvent: LogEvent,
    payload: {
        step: ValidationStep;
        attemptId?: string;
        paymentMethod?: string;
        userId?: string | null;
        sections?: Record<string, FieldErrors>;
    }
): void {
    const { sections, ...rest } = payload;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.VALIDATION_FAILED,
        // The customer mistyping is the overwhelming majority of these. A field
        // broken for everyone shows up in `invalidFields`, not as an error.
        level: "warn",
        data: {
            result: CHECKOUT_RESULT.BLOCKED,
            stage: CHECKOUT_STAGE.VALIDATION,
            component: CHECKOUT_COMPONENT.FORM,
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.VALIDATION,
            reason: "invalid_fields",
            customerImpact: CHECKOUT_CUSTOMER_IMPACT.CHECKOUT_BLOCKED,
            ...rest,
            ...(sections
                ? { invalidFields: invalidFieldNames(sections) }
                : {}),
            ...deviceContext(),
        },
    });
}

/**
 * Why a submit was refused before any payment was attempted. Unlike
 * `checkout_validation_failed`, these are states of our own application that
 * the PURCHASE button was clickable in anyway. A wire contract (see
 * `TOKENIZE_OUTCOME`), snake_case rather than the prose
 * `EXPRESS_CTA_BLOCK_REASON` uses for BigQuery.
 */
export const SUBMIT_BLOCK_REASON = {
    /** The re-entry guard refused a second click before the button disabled. */
    ALREADY_SUBMITTING: "already_submitting",
    /** No purchasable price item could be resolved from the cart. */
    NO_PRICE_ITEM: "no_price_item",
    /** An authenticated or upsell submit with no saved payment source. */
    NO_PAYMENT_SOURCE: "no_payment_source",
    /**
     * A wallet sheet had taken over the checkout. While a handoff is stuck,
     * this is all the customer's clicks produce.
     */
    WALLET_HANDOFF: "wallet_handoff",
} as const;

export type SubmitBlockReason =
    (typeof SUBMIT_BLOCK_REASON)[keyof typeof SUBMIT_BLOCK_REASON];

/**
 * A double click is the guard doing its job, so it is not a defect. The other
 * reasons are states the button should never have been clickable in: someone
 * pressed PURCHASE on nothing to buy, or with no way to pay.
 */
const EXPECTED_SUBMIT_BLOCK_REASONS = new Set<SubmitBlockReason>([
    SUBMIT_BLOCK_REASON.ALREADY_SUBMITTING,
    SUBMIT_BLOCK_REASON.WALLET_HANDOFF,
]);

/**
 * A submit that never became an attempt. No `attemptId` by design: every path
 * mints one only after these guards have passed. `cartItemCount` separates two
 * defects sharing one branch — an empty cart is lost state, a cart with items
 * but no price item is bad product data.
 */
export function logCheckoutSubmitBlocked(
    logEvent: LogEvent,
    payload: {
        flow: CheckoutFlow;
        paymentFlow?: CheckoutPaymentFlow;
        reason: SubmitBlockReason;
        cartItemCount?: number;
        paymentMethod?: string;
        userId?: string | null;
    }
): void {
    const { flow, paymentFlow, reason, ...rest } = payload;
    const expected = EXPECTED_SUBMIT_BLOCK_REASONS.has(reason);

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.SUBMIT_BLOCKED,
        level: expected ? "info" : "error",
        data: {
            result: CHECKOUT_RESULT.BLOCKED,
            stage: CHECKOUT_STAGE.SUBMIT,
            component: CHECKOUT_COMPONENT.FORM,
            ...checkoutDimensions(flow, paymentFlow),
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.STATE,
            reason,
            customerImpact: expected
                ? CHECKOUT_CUSTOMER_IMPACT.NONE
                : CHECKOUT_CUSTOMER_IMPACT.CHECKOUT_BLOCKED,
            ...rest,
            ...deviceContext(),
        },
    });
}

/** Card tokenize stage begins: payment intent is about to be created. */
export function logTokenizeStart(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        paymentFlow?: CheckoutPaymentFlow;
        paymentMethod?: string;
        userId?: string | null;
        amount?: number;
    }
): void {
    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.TOKENIZE_START,
        level: "info",
        data: {
            result: CHECKOUT_RESULT.STARTED,
            stage: CHECKOUT_STAGE.TOKENIZE,
            component:
                payload.paymentMethod === "card"
                    ? CHECKOUT_COMPONENT.HOSTED_CARD_FIELDS
                    : CHECKOUT_COMPONENT.WALLET_HANDLER,
            ...payload,
            ...deviceContext(),
        },
    });
}

/**
 * Card tokenize stage finished. Failures (gateway declines included) happen
 * browser↔ChargeBee↔Stripe without touching our API, so this event is the only
 * first-party record of them.
 */
export function logTokenizeResult(
    logEvent: LogEvent,
    payload: {
        attemptId: string;
        paymentFlow?: CheckoutPaymentFlow;
        outcome: TokenizeOutcome;
        durationMs: number;
        paymentMethod?: string;
        userId?: string | null;
        paymentIntentId?: string;
        httpStatus?: CheckoutHttpStatus;
        dismissalSource?: WalletDismissalSource;
        error?: unknown;
    } & Partial<CardTokenizeErrorContext> &
        Partial<CardAuthorizeTelemetry> &
        Partial<TokenizePhaseTelemetry> &
        Partial<CardFieldsTelemetry> &
        Partial<WalletSheetTelemetry>
): void {
    const { error, ...rest } = payload;
    const nonFailure = NON_FAILURE_TOKENIZE_OUTCOMES.has(payload.outcome);
    const addressRejected = payload.outcome === TOKENIZE_OUTCOME.ADDRESS_REJECTED;
    const blocked =
        payload.outcome === TOKENIZE_OUTCOME.BLOCKED || addressRejected;
    const intentIntercepted =
        payload.outcome === TOKENIZE_OUTCOME.INTENT_INTERCEPTED;
    const intentFailure =
        payload.outcome === TOKENIZE_OUTCOME.INTENT_FAILED ||
        payload.outcome === TOKENIZE_OUTCOME.INTENT_ERROR ||
        intentIntercepted;
    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.TOKENIZE_RESULT,
        level: nonFailure
            ? "info"
            : addressRejected
                ? "warn"
                : resolveGatewayFailureLevel(
                    payload.failureCategory,
                    payload.paymentMethod
                ),
        data: {
            result:
                payload.outcome === TOKENIZE_OUTCOME.SUCCESS
                    ? CHECKOUT_RESULT.SUCCESS
                    : payload.outcome === TOKENIZE_OUTCOME.DISMISSED
                        ? CHECKOUT_RESULT.DISMISSED
                        : blocked
                            ? CHECKOUT_RESULT.BLOCKED
                            : CHECKOUT_RESULT.FAILURE,
            stage: intentFailure
                ? CHECKOUT_STAGE.PAYMENT_INTENT
                : CHECKOUT_STAGE.TOKENIZE,
            component: intentFailure
                ? CHECKOUT_COMPONENT.PAYMENT_INTENT
                : payload.paymentMethod === "card"
                    ? CHECKOUT_COMPONENT.HOSTED_CARD_FIELDS
                    : CHECKOUT_COMPONENT.WALLET_HANDLER,
            ...(!nonFailure
                ? {
                    failureBoundary: intentFailure
                        ? payload.outcome === TOKENIZE_OUTCOME.INTENT_ERROR
                            ? CHECKOUT_FAILURE_BOUNDARY.NETWORK
                            : intentIntercepted
                                ? CHECKOUT_FAILURE_BOUNDARY.VERCEL_EDGE
                                : CHECKOUT_FAILURE_BOUNDARY.INNERBALANCE_API
                        : payload.outcome === TOKENIZE_OUTCOME.CARD_NOT_MOUNTED ||
                              blocked
                            ? CHECKOUT_FAILURE_BOUNDARY.CLIENT
                            : CHECKOUT_FAILURE_BOUNDARY.CHARGEBEE,
                    failureCategory:
                          payload.failureCategory ??
                          (intentFailure
                              ? payload.outcome === TOKENIZE_OUTCOME.INTENT_ERROR
                                  ? CHECKOUT_FAILURE_CATEGORY.NETWORK
                                  : intentIntercepted
                                      ? CHECKOUT_FAILURE_CATEGORY.EDGE_INTERCEPTION
                                      : CHECKOUT_FAILURE_CATEGORY.API
                              : payload.outcome ===
                                  TOKENIZE_OUTCOME.CARD_NOT_MOUNTED
                                  ? CHECKOUT_FAILURE_CATEGORY.STATE
                                  : addressRejected
                                      ? CHECKOUT_FAILURE_CATEGORY.VALIDATION
                                      : CHECKOUT_FAILURE_CATEGORY.SDK),
                    reason: payload.outcome,
                    customerImpact: blocked
                        ? CHECKOUT_CUSTOMER_IMPACT.CHECKOUT_BLOCKED
                        : CHECKOUT_CUSTOMER_IMPACT.PAYMENT_FAILED,
                }
                : {
                    customerImpact: CHECKOUT_CUSTOMER_IMPACT.NONE,
                }),
            ...rest,
            ...(error === undefined || error === null
                ? {}
                : {
                    error: serializeError(error),
                    errorName: allowlistedErrorName(error),
                }),
            ...deviceContext(),
        },
    });
}

/**
 * How a coupon application ended. A wire contract like `TOKENIZE_OUTCOME`, so
 * the tests assert the literals rather than importing them.
 */
export const COUPON_APPLY_OUTCOME = {
    /** The API accepted the code and the discount is on the order. */
    APPLIED: "applied",
    /** The API answered and refused it: spent, expired, typo, wrong plan. */
    REJECTED: "rejected",
    /**
     * Non-OK result that is not a validation verdict — 5xx, auth, routing, an
     * edge page, or a request the api client gave up on without an answer.
     */
    API_ERROR: "api_error",
    /** The request threw: network, timeout, or a malformed response. */
    EXCEPTION: "exception",
} as const;

export type CouponApplyOutcome =
    (typeof COUPON_APPLY_OUTCOME)[keyof typeof COUPON_APPLY_OUTCOME];

/**
 * Outcomes that are not our defect: a spent, expired or ineligible code is the
 * system working as designed, and does not belong at error level.
 */
const COUPON_BUSINESS_OUTCOMES = new Set<CouponApplyOutcome>([
    COUPON_APPLY_OUTCOME.APPLIED,
    COUPON_APPLY_OUTCOME.REJECTED,
]);

/**
 * The level a settled coupon application deserves.
 *
 * `EXCEPTION` goes through the same benign-failure policy the product fetch
 * uses, rather than being levelled on the outcome alone. A customer who typed
 * a code with no connection, or who navigated away mid-request, throws here
 * exactly as an outage does, and there are far more of the first kind. Warn
 * keeps them queryable without filing each one as something to fix.
 */
function resolveCouponApplyLevel(
    outcome: CouponApplyOutcome,
    error: unknown,
    failureReason: FetchFailureReason | undefined
): LogLevel {
    if (COUPON_BUSINESS_OUTCOMES.has(outcome)) return "info";

    if (outcome === COUPON_APPLY_OUTCOME.EXCEPTION) {
        return isBenignFetchFailure(error, failureReason) ? "warn" : "error";
    }

    return "error";
}

/**
 * A coupon application settled. Emitted for every source, not just typed
 * codes: a URL coupon that silently fails to apply charges the customer full
 * price without ever showing an error.
 */
export function logCouponApplyResult(
    logEvent: LogEvent,
    payload: {
        outcome: CouponApplyOutcome;
        source: string;
        couponCode: string;
        priceItemSlug?: string;
        /** The status the server answered with; absent when none did. */
        httpStatus?: number;
        /** An edge page answered in our API's place (`isEdgeInterception`). */
        edgeIntercepted?: boolean;
        couponError?: string;
        error?: unknown;
    }
): void {
    const {
        error,
        couponError,
        couponCode,
        edgeIntercepted = false,
        ...rest
    } = payload;
    // Only for a throw: a response that came back and refused is not a fetch
    // failure, and classifying it would file a 503 as `network`.
    const failureReason =
        payload.outcome === COUPON_APPLY_OUTCOME.EXCEPTION
            ? classifyFetchFailure(error)
            : undefined;

    const applied = payload.outcome === COUPON_APPLY_OUTCOME.APPLIED;
    const rejected = payload.outcome === COUPON_APPLY_OUTCOME.REJECTED;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.COUPON_APPLY_RESULT,
        level: resolveCouponApplyLevel(payload.outcome, error, failureReason),
        data: {
            result:
                applied
                    ? CHECKOUT_RESULT.SUCCESS
                    : rejected
                        ? CHECKOUT_RESULT.BLOCKED
                        : CHECKOUT_RESULT.FAILURE,
            stage: CHECKOUT_STAGE.COUPON,
            component: CHECKOUT_COMPONENT.COUPON,
            ...(applied
                ? {
                    customerImpact: CHECKOUT_CUSTOMER_IMPACT.NONE,
                }
                : rejected
                    ? {
                        failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
                        failureCategory:
                            CHECKOUT_FAILURE_CATEGORY.VALIDATION,
                        customerImpact: CHECKOUT_CUSTOMER_IMPACT.NONE,
                    }
                    : {
                        ...(payload.outcome === COUPON_APPLY_OUTCOME.EXCEPTION
                            ? {
                                failureBoundary:
                                    CHECKOUT_FAILURE_BOUNDARY.NETWORK,
                                failureCategory:
                                    CHECKOUT_FAILURE_CATEGORY.NETWORK,
                            }
                            : resolveApiFailureAttribution(
                                payload.httpStatus,
                                edgeIntercepted
                            )),
                        customerImpact: CHECKOUT_CUSTOMER_IMPACT.DEGRADED,
                    }),
            reason: payload.outcome,
            ...rest,
            failureReason,
            // The promo box is free text: a mistyped email or a pasted phone
            // number arrives here as readily as "SAVE10". A real code has no
            // `@` and no seven-digit run, so redaction leaves it untouched.
            couponCode: redactPersonalData(couponCode),
            couponError: serializeServerMessage(couponError),
            error: serializeError(error),
            ...deviceContext(),
        },
    });
}

/**
 * The checkout gave up waiting for the coupon and rendered a total the customer
 * was not promised.
 *
 * Only one shape of this costs anyone money: a coupon was named in the URL and
 * five seconds later none was applied, so the page quotes full price for an
 * offer that promised less. That is the `error`.
 *
 * Everything else the deadline catches is the deadline doing its job — no
 * coupon was ever requested, or one is already applied and a later write was
 * still settling. Levelling those as errors would file one record for every
 * slow mobile connection that opened checkout, which is the opposite of what
 * the error level is for. They stay queryable at `warn`.
 */
export function logCouponResolutionTimeout(
    logEvent: LogEvent,
    payload: {
        timeoutMs: number;
        hasCouponApplied: boolean;
        urlCouponId?: string | null;
        productSlug?: string;
    }
): void {
    const isUnappliedUrlCoupon =
        Boolean(payload.urlCouponId) && !payload.hasCouponApplied;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.COUPON_RESOLUTION_TIMEOUT,
        level: isUnappliedUrlCoupon ? "error" : "warn",
        data: {
            result: CHECKOUT_RESULT.FAILURE,
            stage: CHECKOUT_STAGE.COUPON,
            component: CHECKOUT_COMPONENT.COUPON,
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.TIMEOUT,
            reason: "resolution_timeout",
            customerImpact: isUnappliedUrlCoupon
                ? CHECKOUT_CUSTOMER_IMPACT.DEGRADED
                : CHECKOUT_CUSTOMER_IMPACT.NONE,
            ...payload,
            ...deviceContext(),
        },
    });
}

/**
 * The charge went through and the navigation to /thank-you threw. A distinct
 * event because filing it as `request_threw` would count a completed payment
 * among attempts that never got an answer; level `error` because the client
 * router failed a paid customer, even though `redirectToThankYou` recovers them
 * with a full-page load.
 *
 * The router navigates asynchronously, so only a synchronous throw is
 * caught here. A transition that never arrives is found instead as a 200
 * `checkout_response` with no `thankyou_navigated` for the same `attemptId`.
 */
export function logThankYouRedirectFailed(
    logEvent: LogEvent,
    payload: {
        attemptId?: string;
        userId?: string | null;
        error?: unknown;
    }
): void {
    const { error, ...rest } = payload;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.THANK_YOU_REDIRECT_FAILED,
        level: "error",
        data: {
            result: CHECKOUT_RESULT.FAILURE,
            stage: CHECKOUT_STAGE.REDIRECT,
            component: CHECKOUT_COMPONENT.NAVIGATION,
            failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
            failureCategory: CHECKOUT_FAILURE_CATEGORY.NAVIGATION,
            reason: "redirect_failed",
            customerImpact:
                CHECKOUT_CUSTOMER_IMPACT.CHARGED_WITHOUT_CONFIRMATION,
            ...rest,
            // Name only: a live `Error` handed to `logEvent` arrives as `{}`,
            // because the SDK copies enumerable own properties and
            // `message`/`stack` are neither. See `allowlistedErrorName` for
            // why the raw message is not ours to send.
            errorName: allowlistedErrorName(error),
            ...deviceContext(),
        },
    });
}

/**
 * Funnel-closing success event, emitted on /thank-you mount. `attemptId` is
 * read back from localStorage, so it may be absent on direct navigation.
 */
export function logThankYouNavigated(
    logEvent: LogEvent,
    payload: {
        attemptId?: string;
        userId?: string | null;
        externalSubscriptionId?: string | null;
        msFromSubmit?: number | null;
    }
): void {
    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.THANK_YOU_NAVIGATED,
        level: "info",
        data: {
            result: CHECKOUT_RESULT.SUCCESS,
            stage: CHECKOUT_STAGE.REDIRECT,
            component: CHECKOUT_COMPONENT.NAVIGATION,
            customerImpact: CHECKOUT_CUSTOMER_IMPACT.NONE,
            ...payload,
            ...deviceContext(),
        },
    });
}

/** Which SDK became usable, and how far it got. A wire contract. */
export const CHECKOUT_SDK_STAGE = {
    /** The `<script>` arrived and exposed the global we validate for. */
    SCRIPT_LOADED: "script_loaded",
    /** ChargeBee's own `init`/`getInstance` returned a usable instance. */
    INITIALIZED: "initialized",
} as const;

export type CheckoutSdkStage =
    (typeof CHECKOUT_SDK_STAGE)[keyof typeof CHECKOUT_SDK_STAGE];

/**
 * A payment SDK became usable.
 *
 * The failure side of this has been reported for a while — the
 * `checkout_external_script_*` pair, `checkout_card_fields_mount_timeout`,
 * `checkout_wallet_prerequisites_missing` — with nothing to read it against. A load
 * that got three seconds slower for a tenth of our customers moves no failure
 * count at all; it moves this distribution, and `attempt` says whether the
 * retry chain is quietly carrying the difference.
 *
 * Info because the SDK working is the normal case. Having the normal case on
 * record is the entire point: without it every failure count is a numerator
 * with no denominator.
 */
export function logCheckoutSdkReady(
    logEvent: LogEvent,
    payload: {
        /**
         * Which SDK. Named to match the `scriptId` the two
         * `checkout_external_script_*` events already carry, so success and failure
         * for one script are a single query rather than two field names for
         * the same value.
         */
        scriptId: string;
        stage: CheckoutSdkStage;
        /** Time to ready, measured from the mount that asked for the SDK. */
        loadMs: number;
        /**
         * The SDK was already on `window` when the mount started, so nothing
         * was fetched and `loadMs` is 0.
         *
         * Two populations, and only one of them is a load: filter these out
         * before reading load latency, or a page that remounts the script
         * often will report a percentile for fetches that never happened.
         * Always present, so `false` is an answer rather than an absent field.
         */
        preloaded?: boolean;
        /**
         * 1 when the first load succeeded; higher means the retry chain
         * recovered it, which is invisible today because only the terminal
         * failure is reported.
         */
        attempt?: number;
        /** On the visit clock — see `cardFieldsReadyAtVisitMs`. */
        readyAtVisitMs?: number;
    }
): void {
    const { stage: sdkStage, ...rest } = payload;

    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.SDK_READY,
        level: "info",
        data: {
            result: CHECKOUT_RESULT.SUCCESS,
            stage: CHECKOUT_STAGE.SDK_LOAD,
            component: CHECKOUT_COMPONENT.CHARGEBEE_SDK,
            customerImpact: CHECKOUT_CUSTOMER_IMPACT.NONE,
            sdkStage,
            ...rest,
            ...deviceContext(),
        },
    });
}

/**
 * The customer advanced from contact details to payment.
 *
 * The step boundary has only ever gone to BigQuery, so a Datadog-only
 * question — "a customer reports being stuck on checkout, which half were they
 * in?" — has no answer. It also gives `checkout_validation_failed` on the
 * contact step a denominator, which it has never had.
 */
export function logCheckoutStepAdvanced(
    logEvent: LogEvent,
    payload: {
        from: number;
        to: number;
        userId?: string | null;
        /** On the visit clock: how long the contact step took to clear. */
        msFromVisitStart?: number;
    }
): void {
    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.STEP_ADVANCED,
        level: "info",
        data: {
            result: CHECKOUT_RESULT.SUCCESS,
            stage: CHECKOUT_STAGE.CONTACT,
            component: CHECKOUT_COMPONENT.FORM,
            customerImpact: CHECKOUT_CUSTOMER_IMPACT.NONE,
            ...payload,
            ...deviceContext(),
        },
    });
}

/**
 * The page went away with the blocking overlay still up.
 *
 * Closes the hole `checkout_overlay_stuck` cannot reach: that watchdog needs
 * two minutes, so a tab closed at thirty seconds leaves a
 * `checkout_submit_start` with no terminal event — which reads exactly like
 * our own code hanging forever. Emitted on `pagehide`, the last point a
 * browser guarantees, and the Browser Logs SDK flushes it with `sendBeacon`.
 *
 * Named for what the browser told us, not for what it meant. `pagehide` also
 * fires on a bfcache freeze and on a hard navigation away from a charge that
 * went through, and at emit time nothing here can tell those from a customer
 * giving up. Calling the record `abandoned` would have made that guess in the
 * event name, where no later query can undo it; `paymentConfirmed` and
 * `hasServerError` carry the evidence instead, and the classification is made
 * when reading against the attempt's other records.
 *
 * Info, not a failure level: leaving is the customer's decision and most of
 * these are a wallet sheet someone walked away from. It is a funnel
 * terminator, not a defect — `paymentConfirmed` is what marks the shape that
 * costs us, gone while we held them behind a spinner after taking the money.
 */
export function logCheckoutOverlayPageExit(
    logEvent: LogEvent,
    payload: {
        attemptId?: string | null;
        /** How long the overlay had been up when they left. */
        elapsedMs: number;
        isProcessing: boolean;
        isWalletHandoff: boolean;
        /** A charge we know completed, so this is a lost redirect, not a lost sale. */
        paymentConfirmed: boolean;
        /** An error was on screen, so they left a failure rather than a hang. */
        hasServerError: boolean;
        userId?: string | null;
    }
): void {
    logCheckoutEvent(logEvent, {
        message: CHECKOUT_LOG_EVENT.OVERLAY_PAGE_EXIT,
        level: "info",
        data: {
            result: payload.paymentConfirmed
                ? CHECKOUT_RESULT.FAILURE
                : CHECKOUT_RESULT.CANCELLED,
            stage: CHECKOUT_STAGE.REDIRECT,
            component: CHECKOUT_COMPONENT.OVERLAY,
            ...(payload.paymentConfirmed
                ? {
                    failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
                    failureCategory:
                          CHECKOUT_FAILURE_CATEGORY.NAVIGATION,
                    reason: "page_exit_after_payment",
                    customerImpact:
                          CHECKOUT_CUSTOMER_IMPACT.CHARGED_WITHOUT_CONFIRMATION,
                }
                : {
                    customerImpact: CHECKOUT_CUSTOMER_IMPACT.NONE,
                }),
            ...payload,
            ...deviceContext(),
        },
    });
}
